import React, { useCallback, useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Alert, Box, Button, Checkbox, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { Dayjs, toDayJs } from '~/services/TimeService';

import { countNights, formatDateRange } from '~/utils/dateRangeUtils';
import { pluralizeToString } from '~/utils/stringUtils';

interface Props {
  start: string;
  end: string;
  selected: boolean;
  disabled?: boolean;
  onToggle: (value: boolean) => void;
  onEdit?: (newRange: { start: string; end: string }) => void;
  onDelete: () => void;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  chipSx?: object;
  editLabel?: string;
  deleteLabel?: string;
  caption?: React.ReactNode;
}

export default function EditableDateRow({
  start,
  end,
  selected,
  disabled = false,
  onToggle,
  onEdit,
  onDelete,
  children,
  icon,
  chipSx,
  editLabel,
  deleteLabel,
  caption,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editStart, setEditStart] = useState<Dayjs | null>(null);
  const [editEnd, setEditEnd] = useState<Dayjs | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleEditClick = useCallback(() => {
    setEditStart(toDayJs(start));
    setEditEnd(toDayJs(end));
    setSaveError(null);
    setIsEditing(true);
  }, [start, end]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setSaveError(null);
  }, []);

  const handleSave = useCallback(() => {
    if (!editStart || !editEnd || !editStart.isValid() || !editEnd.isValid()) return;
    if (editEnd.isBefore(editStart, 'day')) {
      setSaveError('End date must be on or after start date');
      return;
    }
    onEdit?.({ start: editStart.format('YYYY-MM-DD'), end: editEnd.format('YYYY-MM-DD') });
    setIsEditing(false);
    setSaveError(null);
  }, [editStart, editEnd, onEdit]);

  if (isEditing) {
    return (
      <Stack gap={0.5}>
        <Stack direction="row" gap={1} alignItems="center">
          <DatePicker
            label="From"
            value={editStart}
            onChange={setEditStart}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker label="To" value={editEnd} onChange={setEditEnd} slotProps={{ textField: { size: 'small' } }} />
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={
              !editStart || !editEnd || !editStart.isValid() || !editEnd.isValid() || editEnd.isBefore(editStart, 'day')
            }
          >
            Save
          </Button>
          <Button variant="text" size="small" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
        {saveError && (
          <Alert severity="error" sx={{ py: 0 }}>
            {saveError}
          </Alert>
        )}
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1}>
        <Checkbox size="small" checked={selected} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
        {icon}
        <Typography variant="body2" color="text.secondary">
          {formatDateRange(start, end)}
        </Typography>
        <Chip label={pluralizeToString('night', countNights(start, end))} size="small" sx={chipSx} />
        {caption}
        {onEdit && (
          <IconButton size="small" aria-label={editLabel ?? 'Edit date range'} onClick={handleEditClick}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" aria-label={deleteLabel ?? 'Delete date range'} onClick={onDelete}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      {!isEditing && children}
    </Box>
  );
}
